import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Grid from "../../atoms/Grid/Grid";
import { endpoints } from "../../../api/endpoints";
import {
  getCustomerLoginAs,
  getInvoicesByFilter,
} from "../../../store/slices/CustomerSlice/CustomerSlice";
import { doInvoiceAction } from "../../../store/slices/OrderSlice/OrderSlice";

const invoiceHeaderAttributes = [
  {
    id: "invoice_num",
    label: "Invoice #",
    tooltip: "Invoice Number",
    sortable: true,
    searchable: {
      label: "Search by Invoice Number",
      type: "text",
      width: 4,
    },
    width: "10%",
  },
  {
    id: "type",
    label: "Invoice Type",
    sortable: true,
    width: "10%",
  },
  {
    id: "ref_number",
    label: "Ref Order/Credit #",
    sortable: false,
    searchable: {
      label: "Search by Reference Number",
      type: "text",
      width: 4,
    },
    width: "12%",
  },
  {
    id: "customer_po",
    label: "PO",
    sortable: false,
    searchable: {
      label: "Search by Customer PO",
      type: "text",
      width: 4,
    },
    width: "8%",
  },
  {
    id: "date_invoiced",
    label: "Date",
    tooltip: "Date of Invoice",
    sortable: true,
    width: "10%",
  },
  {
    id: "total",
    label: "Total",
    sortable: true,
    width: "10%",
  },
  {
    id: "customer",
    label: "Customer Summary",
    sortable: true,
    width: "15%",
  },
  {
    id: "customer_alt",
    label: "Customer Billing Name",
    sortable: true,
    width: "12%",
  },
  {
    id: "aftermarket",
    label: "After Market #",
    sortable: true,
    width: "8%",
  },
  {
    id: "parent_num",
    label: "Parent #",
    sortable: true,
    width: "8%",
  },
  {
    id: "details",
    label: "Details",
    sortable: false,
    width: "7%",
  },
];

const returnRealAttributeName = (attr) => {
  switch (attr) {
    case "invoice_num":
      return "invoice_num";
    case "ref_number":
      return "customer_order_num";
    case "customer_po":
      return "customer_po";
    case "date_invoiced":
      return "date_invoiced";
    case "customer":
      return "customer_name";
    case "customer_alt":
      return "customer_bill_name";
    case "aftermarket":
      return "customer_aftermarket_network_num";
    case "parent_num":
      return "customer_parent_num";
    default:
      return attr;
  }
};

const mapInvoiceData = (invoices, dispatch) => {
  const handleDownloadInvoice = (invoiceNum) => {
    dispatch(
      doInvoiceAction({
        transactionNum: invoiceNum,
        transactionNumType: "invoice",
        deliveryMethod: "pdf",
        emailAddress: "",
        useOverride: false,
        showPricing: true,
      })
    );
  };

  return invoices.map((d) => ({
    id: d.invoice_num,
    invoice_num: (
      <Link
        to={`${endpoints.ACCOUNTS_RECEIVABLE_ENTRY}/${d.invoice_num}/${d.customer_num}`}
        target="_blank"
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {d.invoice_num}
      </Link>
    ),
    type: (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          d.type === 1
            ? "bg-red-100 text-red-800"
            : "bg-green-100 text-green-800"
        }`}
      >
        {d.type === 1 ? "Credit" : "Order"}
      </span>
    ),
    ref_number: d.customer_order_num || "-",
    customer_po: d.customer_po || "-",
    date_invoiced: d.date_invoiced
      ? dayjs(d.date_invoiced).format("MM/DD/YYYY")
      : "-",
    total: d.total_invoiced
      ? `$${parseFloat(d.total_invoiced).toFixed(2)}`
      : "$0.00",
    customer:
      d.customer_num && d.customer_name ? (
        <Link
          to={`/customers/${d.customer_num}`}
          className="text-[var(--primary-color)] hover:text-[var(--primary-color-dark)] underline"
        >
          {`${d.customer_num} - ${d.customer_name}`}
        </Link>
      ) : (
        <span>-</span>
      ),
    customer_alt: d.customer_bill_name || "-",
    aftermarket:
      d.customer_aftermarket_network_num > 0
        ? d.customer_aftermarket_network_num
        : "-",
    parent_num: d.customer_parent_num > 0 ? d.customer_parent_num : "-",
    details: (
      <button
        onClick={() => handleDownloadInvoice(d.invoice_num)}
        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[var(--primary-color)] hover:bg-[var(--primary-color-dark)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)]"
      >
        Download
      </button>
    ),
  }));
};

export default function InvoiceHistory() {
  const dispatch = useDispatch();
  const { loginOptions } = useSelector((state) => state.customer);

  // Fetch customer list on component mount
  useEffect(() => {
    dispatch(getCustomerLoginAs());
  }, [dispatch]);

  const fetchInvoiceData = useCallback(
    async (params) => {
      const invoiceParams = {
        invoice_num: params.invoice_num,
        customer_num: params.customerNumbers, // This will be comma-delimited string
        ref_number: params.ref_number,
        customer_po: params.customer_po,
        search: params.search,
        page: params.page,
        order_by: returnRealAttributeName(params.sortBy),
        sort_order: params.sortOrder === "desc" ? "dsc" : "asc",
        from: params.from,
        to: params.to,
      };

      const result = await dispatch(getInvoicesByFilter(invoiceParams));

      if (!result?.error) {
        return {
          count: result.payload.count,
          results: result.payload.results,
        };
      }

      return { count: 0, results: [] };
    },
    [dispatch]
  );

  const mapDataFunction = useCallback(
    (invoices) => mapInvoiceData(invoices, dispatch),
    [dispatch]
  );

  // Transform loginOptions to dropdown options
  const customerOptions =
    loginOptions?.map((customer) => ({
      id: customer.id.toString(),
      value: customer.id.toString(),
      label: `${customer.id} - ${customer.label}`,
      customerName: customer.label,
    })) || [];

  const customFilters = [
    {
      id: "customerNumbers",
      label: "Filter by Customer Numbers",
      placeholder: "Select customer numbers...",
      type: "autocomplete-multiselect",
      options: customerOptions,
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-4 text-sm text-gray-600 italic">
        <i>Customer PO requires at LEAST 3 characters before searching.</i>
      </div>
      <div className="mt-6">
        <Grid
          title="Invoice History"
          headerAttributes={invoiceHeaderAttributes}
          fetchDataFunction={fetchInvoiceData}
          mapDataFunction={mapDataFunction}
          loadingSelector={(state) => state.loaderSpinner?.loading}
          rowsPerPage={50}
          defaultSortBy="invoice_num"
          defaultSortOrder="desc"
          showFilters={true}
          showDateFilter={true}
          showPagination={true}
          customFilters={customFilters}
        />
      </div>
    </div>
  );
}
